import { isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
  signal,
  OnInit,
  Inject,
  booleanAttribute,
  HostBinding
} from '@angular/core';
import { faHouseUser, faLocationDot, faMapMarker, faSearch } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Subscription, firstValueFrom, fromEvent, lastValueFrom } from 'rxjs';
import { filter, first, tap, throttleTime } from 'rxjs/operators';

import { Environment, ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { Country, LocationCoords, LocationComplete } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { GoogleMapsLoaderService } from '../google-maps-loader.service';

// DOCS: https://developers.google.com/maps/documentation/javascript/place-autocomplete?hl=es-419

/**
 * Component to change the location of the app updating the {@link LocationService}.
 *
 * NOTE: there are 2 icons attached to the input. Change this with css rules.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LocationSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * @deprecated Don't found any use
   */
  @ViewChild('searchLocation')
  searchElementRef: ElementRef;

  @ViewChild('input') inputRef: ElementRef<HTMLInputElement>;

  /**
   * Emits the location and country selected.
   * //TODO replace the any with the real type of data that emits
   */
  @Output()
  locationSelected = new EventEmitter<LocationComplete>();

  /**
   * Emits the latatiude and longitude of the location selected.
   * //TODO refractor name or check if can be merged with {@link locationSelected}.
   */
  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  onChange = new EventEmitter<LocationCoords>();

  /**
   * Element reference of the input text
   */
  @ViewChild('places_autocomplete')
  placesAutocomplete: ElementRef;

  @Input({ transform: booleanAttribute }) showMarker = true;

  /**
   * Displays input the icons attached to the input.
   */
  @Input() hasInput = true;

  @Input() editable = true;

  @Input() enableRouterNavigation = true;

  /**
   * Display the "Near me" button.
   */
  @Input()
  hasNearMe = true;

  /**
   * Changes the translate key of the near me button.
   */
  @Input()
  allowText = false;

  /**
   * @deprecard Don't found any use
   */
  @Input()
  defaultPlaceholder = false;

  @Input()
  placesTypes: string[] = ['sublocality', 'movie_theater', 'locality', 'postal_code', 'neighborhood'];

  /**
   * @deprecard Activates a class that is not found in the styles.
   */
  @Input()
  compacted = false;

  /**
   * An example of usage is in the register to change the location after complete the form.
   */
  @Input() noUpdateSelection = false;

  /**
   * This props has sense if is used with {@link noUpdateSelection}.
   * Example of usage: This was used in the feature register form to get the location and country. If the initial
   * location is setted the country will be undefined.
   */
  @Input() initialEmpty = false;

  /**
   * Actual value of the input text ({@link placesAutocomplete}).
   */
  value = '';

  /**
   * Placehoder of the input text ({@link placesAutocomplete})
   */
  placeholderSig = signal<string>('');
  nearMeLabel: string;

  /**
   * @docsNotRequired
   */
  houseIcon = faHouseUser;
  searchIcon = faSearch;
  markerIcon = faMapMarker;
  locationIcon = faLocationDot;

  /**
   * Oversevable to the {@link GoogleMapsLoaderService} to know when the google maps api is ready.
   * Makes ready the {@link placesAutocomplete} to search places.
   */
  private placesSubscription: Subscription;

  /**
   * Observable that listen when a place is selected. Updates the location and country of the {@link LocationService}.
   */
  private autocompleteListener: google.maps.MapsEventListener;

  private afterNgAfterViewInit = new BehaviorSubject(false);
  private eventSub: Subscription;
  readonly countryBasedApplicationEnabled = this.env.countryBasedApplication?.enabled;

  @HostBinding('class') locationSelectorContainerType: 'container-type-normal' | 'container-type-inline-size';

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private channelService: ChannelService,
    private ngZone: NgZone,
    private loc: Location,
    private locationService: LocationService,
    private trackingService: TrackingService,
    private googleMapsLoader: GoogleMapsLoaderService,
    private translationsService: TranslationsService,
    private cd: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId
  ) {
    this.locationSelectorContainerType =
      this.env?.customPageParams?.locationSelectorContainerType || 'container-type-inline-size';
    this.placeholder = this.translationsService.translate('public.filters.location.placeholder', 'Enter a location…');
    lastValueFrom(this.afterNgAfterViewInit.pipe(first(v => v))).then(() => {
      this.initOnBrowser();
    });
  }

  ngOnInit() {
    this.setNearMeLabel();

    if (isPlatformBrowser(this.platformId)) {
      this.eventSub = fromEvent(window, 'scroll')
        .pipe(
          throttleTime(300) // Emits scroll events no more frequently than every 300ms
        )
        .subscribe(() => this.repositionPacContainer());
    }
  }

  repositionPacContainer() {
    const pacContainer = document.querySelector('.pac-container');
    const inputElement = this.placesAutocomplete?.nativeElement;

    if (pacContainer instanceof HTMLElement && inputElement instanceof HTMLElement) {
      const rect = inputElement.getBoundingClientRect();
      pacContainer.style.position = 'absolute';
      pacContainer.style.top = `${rect.bottom + window.scrollY}px`;
      pacContainer.style.left = `${rect.left + window.scrollX}px`;
      pacContainer.style.width = `${rect.width}px`;
    }
  }

  ngAfterViewInit() {
    this.afterNgAfterViewInit.next(true);
    this.blurInputOnEnter();
  }

  initOnBrowser() {
    this.setInitialLocation();
    this.setGooglePlacesAutocomplete();
    // this.setStylePacContainerFix();
  }

  setGooglePlacesAutocomplete() {
    const self = this;
    this.placesSubscription = this.googleMapsLoader.$mapsLoaded
      .pipe(filter(elem => !!elem))
      .subscribe(mapsAvailable => {
        if (mapsAvailable) {
          const autoCompleteInput = this.placesAutocomplete.nativeElement;
          const autocompleteEl = new google.maps.places.Autocomplete(autoCompleteInput, {
            fields: ['formatted_address', 'geometry.location'],
            types: this.placesTypes,
            componentRestrictions: this.getCountryRestrictions()
          });
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const that = this;
          this.autocompleteListener = google.maps.event.addListener(autocompleteEl, 'place_changed', function () {
            const place = autocompleteEl.getPlace();
            if (place && place.geometry) {
              const location = place.geometry.location;
              const address = place.formatted_address;
              const latitude = location.lat();
              const longitude = location.lng();
              that.placesAutocomplete.nativeElement.value = address;
              that.setPlaceholder(address);
              if (
                that.locationService.userLocation?.latitude != latitude &&
                that.locationService.userLocation?.longitude != longitude
              ) {
                that.onChange.emit({ latitude, longitude });
              }

              // updating country selected
              that.updateCountrySelected(latitude, longitude).then(country => {
                // the autocomplete listener runs outside the angular context: so events fired from here that end up
                // changing the component models, will not trigger a component re-render. we use 'zone' to force the location
                // notifications to run inside the angular context
                that.ngZone.run(() => {
                  that.updateLocationService({
                    location: { address, latitude, longitude },
                    country
                  });

                  that.trackLocationChanged();
                });
              });
            }
            self.repositionPacContainer();
          });
          setTimeout(() => {
            autoCompleteInput.blur();
            autoCompleteInput.focus();
          }, 100);
        }
      });
  }

  setInitialLocation() {
    if (this.initialEmpty) return;
    if (this.locationService.userLocation) {
      this.value = this.locationService.userLocation.address;
      const longitude = this.locationService.userLocation.longitude;
      const latitude = this.locationService.userLocation.latitude;
      this.onChange.emit({ latitude, longitude });
      this.updateLocationService({
        location: this.locationService.userLocation,
        country: this.locationService.selectedCountry
      });
      this.setPlaceholder();
    } else {
      // Maybe the default location is not loaded at this point
      firstValueFrom(
        this.locationService.$locationChanged.pipe(
          filter(Boolean),
          tap(currentLocation => {
            this.value = currentLocation.address;
            const longitude = currentLocation.longitude;
            const latitude = currentLocation.latitude;
            this.locationService.userLocation = currentLocation;
            this.onChange.emit({ latitude, longitude });
          })
        )
      ).then(() => this.setInitialLocation());
    }
  }

  updateLocationService(locationComplete: LocationComplete) {
    if (!this.noUpdateSelection) {
      const {
        location: { address, latitude, longitude }
      } = locationComplete;
      this.locationService.setUserLocation({
        address,
        latitude,
        longitude
      });
    }

    this.locationSelected.emit(locationComplete);
  }

  getCountryRestrictions() {
    if (this.countryBasedApplicationEnabled) {
      return { country: [this.locationService.selectedCountry.code2] };
    }

    const countriesRestricted = this.channelService.channel.partner.locationRestrictions;
    if (countriesRestricted?.length > 0 && countriesRestricted.length <= 5) {
      const codeArray = countriesRestricted.map(c => c.code2);
      return { country: codeArray };
    }
    return { country: [] };
  }

  /**
   * Updates the {@link placesAutocomplete} placeholder.
   * @param value
   *
   * //TODO is okey the firsts condition? Maybe use the deprecated defaultPlaceholder as initial and put the translate key as initial.
   */
  setPlaceholder(value?: string) {
    if (value) {
      this.placeholder = value;
    } else if (this.locationService.userLocation) {
      this.placeholder = this.locationService?.userLocation.address;
    } else {
      this.placeholder = this.translationsService.translate('public.filters.location.placeholder', 'Enter a location…');
    }
    this.cd.detectChanges();
  }

  /**
   * Sets actual user location.
   */
  setNearMe() {
    this.locationService.findMyLocation().then(location => {
      this.value = location.address;
      this.locationService.setUserLocation(location);
      this.updateCountrySelected(location.latitude, location.longitude).then(country => {
        const location = this.locationService.userLocation;
        this.locationSelected.emit({ location, country });
      });
      this.setPlaceholder();
      this.trackLocationChanged();
    });
  }

  setNearMeLabel() {
    this.nearMeLabel = this.allowText
      ? this.translationsService.translate('public.filters.location.allow', 'Allow')
      : this.translationsService.translate('public.filters.location.nearMe', 'Near me');
  }

  setCustomValue(value: string) {
    this.value = value;
    this.placeholder = value;
  }

  /**
   * Adds the location as query param and adds a track event.
   *
   * //TODO barbeitoalex propostal - refractor name because this is used in any case. And remove allowTracking
   * //TODO fix userLocation label track. Should send the address? Don't have any sense send an object
   */
  private trackLocationChanged() {
    const userLocation = this.locationService.userLocation;
    if (this.enableRouterNavigation) {
      this.updateLatLonParams(userLocation.latitude, userLocation.longitude);
    }

    this.trackingService.trackEvent({
      action: 'FilterLocation',
      properties: {
        label: userLocation ? userLocation.address : 'unknown',
        category: 'MovieFilters'
      }
    });
  }

  updateLatLonParams(lat: number, lon: number): void {
    const currentPath = this.loc.path().split('?')[0];
    const currentParams = new URLSearchParams(this.loc.path().split('?')[1] || '');

    // Update or add lat and lon parameters
    currentParams.set('lat', lat.toString());
    currentParams.set('lon', lon.toString());

    // remove cinema param if exists
    if (currentParams.has('cinema')) {
      currentParams.delete('cinema');
    }
    // Construct the new query string
    const paramsStr = currentParams.toString();

    // Replace the state with the updated query parameters
    this.loc.replaceState(currentPath, paramsStr);
  }

  // updateCountrySelected(latitude, longitude) {
  //   this.usheruApi.getLocationApi(latitude, longitude).subscribe(locationApi => {
  //     this.locationService.selectedCountry = <Country>locationApi.region.country;
  //   });
  // }

  /**
   * Updates the country provided by coordinates.
   *
   * //TODO adds the return type
   * //TODO replace the toPromise with {@link lastValueFrom}.
   * @param latitude
   * @param longitude
   * @returns
   */
  updateCountrySelected(latitude, longitude) {
    return this.locationService
      .getLocationApi(latitude, longitude)
      .toPromise()
      .then(locationApi => {
        const countryLocation = <Country>locationApi.region?.country;
        if (!countryLocation) {
          console.warn('LocationSelectorComponent - updateCountrySelected - countryLocation is undefined', locationApi);
          return this.locationService.selectedCountry;
        } else {
          this.locationService.selectedCountry = countryLocation;
          console.info(
            'LocationSelectorComponent - updateCountrySelected - locationApi.region.country',
            locationApi.region.country
          );
          return this.locationService.selectedCountry;
        }
      });
  }

  /**
   * Generates a style tag to fix the z-index of the pac-container to be visible in primeNG dialogs.
   * This styles don't work if are setted in the styles.global.scss.
   */
  setStylePacContainerFix() {
    if (document.getElementById('pac-container-fix')) return;
    const style = document.createElement('style');
    style.id = 'pac-container-fix';
    style.innerHTML = '.pac-container { z-index: 9999999999 !important; }';
    document.head.appendChild(style);
  }

  /**
   * Used tp clean the {@link placesSubscription} value before start typing.
   * @param event
   * @param isFocusIn
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleFocus(event: any, previousValue?: string) {
    if (!this.googleMapsLoader.$mapsLoaded.value) this.googleMapsLoader.loadGoogleMapsScripts();
    event.target.value = previousValue || '';
  }

  /**
   * Use of this in the listenLocationInputFocus of FeatureRegisterComponent
   */
  blurInputOnEnter() {
    const input = this.inputRef.nativeElement;
    // input.addEventListener('keypress', (e: KeyboardEvent) => {
    //   if (e.key === 'Enter') {
    //     input.blur();
    //   }
    // });
  }

  set placeholder(placeholder: string) {
    this.placeholderSig.set(placeholder);
  }

  /**
   * Ends the listening for {@link placesSubscription} and {@link autocompleteListener}
   */
  ngOnDestroy() {
    this.placesSubscription?.unsubscribe();
    if (this.autocompleteListener) {
      this.autocompleteListener.remove();
    }
  }
}
